import { authGeneration, authSignal, reportSessionExpired } from '../auth/session'

export const TICKET_URL = '/api/access-requests/ticket'
export const CREATION_URL = '/api/access-requests/company-creation'
/** How long the request page's sign-out waits before it offers its retry (code review round 2, F2). */
export const SIGN_OUT_TIMEOUT_MS = 10_000

/** The request a page shows: the person's own, so it carries no id, email or message. */
export interface OwnRequest {
  kind: 'admission' | 'companyCreation'
  status: 'pending' | 'approved' | 'rejected'
  requestedAt: string
}

/** `GET` ticket: the identity the ticket holds, or that it has been admitted since. */
export type TicketView =
  | { kind: 'open'; ticketId: string; email: string; request: OwnRequest | null }
  | { kind: 'admitted'; email: string }

export interface CreationView {
  userId: string
  canCreateCompanies: boolean
  request: OwnRequest | null
}

export type Refusal = 'already_pending' | 'already_refused' | 'already_admitted' | 'already_approved'

/**
 * What a request route ended as.
 *
 * - `stale`: the generation moved on while the request was out; the answer is dropped.
 * - `unauthorized`: a `401`. For the ticket routes it means no live ticket and reports
 *   nothing, since there is no session; for the company-creation routes it is the session
 *   ending, already reported through `reportSessionExpired()`.
 * - `changed`: `409 ticket_changed` or `409 user_changed`, the form was rendered for
 *   another identity and nothing was written.
 * - `refused`: one of the refusals, with the request the person already has when the
 *   route names one.
 */
export type Sent<T> =
  | { status: 'ok'; body: T }
  | { status: 'stale' }
  | { status: 'unauthorized' }
  | { status: 'changed' }
  | { status: 'refused'; code: Refusal; request: OwnRequest | null }
  | { status: 'tooLong' }
  | { status: 'unavailable' }

const REFUSALS: readonly string[] = ['already_pending', 'already_refused', 'already_admitted', 'already_approved']

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null
}

function requestOf(value: unknown): OwnRequest | null {
  const v = record(value)
  if (v === null) return null
  const { kind, status, requestedAt } = v
  if (kind !== 'admission' && kind !== 'companyCreation') return null
  if (status !== 'pending' && status !== 'approved' && status !== 'rejected') return null
  return typeof requestedAt === 'string' ? { kind, status, requestedAt } : null
}

/** `null` and absent are no request; anything else must be one. */
function optionalRequest(value: unknown): OwnRequest | null | undefined {
  if (value === null || value === undefined) return null
  return requestOf(value) ?? undefined
}

/**
 * The one request path of this module, bound to the session's signal and generation like
 * `admin/api.ts`. `expireOn401` is false for the ticket routes: a request page has no
 * session to expire, and it must never reset the auth work of one.
 */
async function send<T>(
  url: string,
  init: RequestInit,
  expireOn401: boolean,
  parse: (body: unknown) => T | null,
): Promise<Sent<T>> {
  const generation = authGeneration()
  let response: Response
  let text: string
  try {
    response = await fetch(url, { credentials: 'same-origin', signal: authSignal(), ...init })
    text = await response.text()
  } catch {
    return generation === authGeneration() ? { status: 'unavailable' } : { status: 'stale' }
  }
  if (generation !== authGeneration()) return { status: 'stale' }
  if (response.status === 401) {
    if (expireOn401) reportSessionExpired()
    return { status: 'unauthorized' }
  }
  if (response.status === 204) {
    const parsed = parse(null)
    return parsed === null ? { status: 'unavailable' } : { status: 'ok', body: parsed }
  }
  let body: unknown
  try {
    body = JSON.parse(text)
  } catch {
    return { status: 'unavailable' }
  }
  const error = record(body)?.error
  if (response.status === 409 && (error === 'ticket_changed' || error === 'user_changed')) return { status: 'changed' }
  if (response.status === 409 && typeof error === 'string' && REFUSALS.includes(error)) {
    const request = optionalRequest(record(body)?.request)
    return request === undefined ? { status: 'unavailable' } : { status: 'refused', code: error as Refusal, request }
  }
  if (response.status === 400 && error === 'message_too_long') return { status: 'tooLong' }
  if (!response.ok) return { status: 'unavailable' }
  const parsed = parse(body)
  return parsed === null ? { status: 'unavailable' } : { status: 'ok', body: parsed }
}

const json = (body: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
})

function createdOf(body: unknown): OwnRequest | null {
  return requestOf(record(body)?.request)
}

export function fetchTicket(): Promise<Sent<TicketView>> {
  return send(TICKET_URL, {}, false, (body) => {
    const v = record(body)
    if (v === null || typeof v.email !== 'string') return null
    if (v.admitted === true) return { kind: 'admitted', email: v.email }
    const request = optionalRequest(v.request)
    if (typeof v.ticketId !== 'string' || request === undefined) return null
    return { kind: 'open', ticketId: v.ticketId, email: v.email, request }
  })
}

export function fileAdmission(ticketId: string, message: string): Promise<Sent<OwnRequest>> {
  return send(TICKET_URL, json({ ticketId, message }), false, createdOf)
}

/** `DELETE` ticket. Only its `204` is a sign-out; anything else keeps the page (F1). */
export function discardTicket(): Promise<Sent<true>> {
  const signal = AbortSignal.any([authSignal(), AbortSignal.timeout(SIGN_OUT_TIMEOUT_MS)])
  return send(TICKET_URL, { method: 'DELETE', signal }, false, (body) => (body === null ? true : null))
}

export function fetchCreation(): Promise<Sent<CreationView>> {
  return send(CREATION_URL, {}, true, (body) => {
    const v = record(body)
    if (v === null || typeof v.userId !== 'string' || typeof v.canCreateCompanies !== 'boolean') return null
    const request = optionalRequest(v.request)
    return request === undefined ? null : { userId: v.userId, canCreateCompanies: v.canCreateCompanies, request }
  })
}

export function fileCreation(userId: string, message: string): Promise<Sent<OwnRequest>> {
  return send(CREATION_URL, json({ userId, message }), true, createdOf)
}
